import {
  GoogleMap,
  InfoWindowF,
  LoadScriptNext,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import doctor from "../../../assets/images/light-logo.svg";
import cursor from "../../../assets/images/icons/locationOnMap.svg";
import markerIcon from "../../../assets/images/icons/routing.svg";
import LaboratoriesLocationOnMap from "../../../assets/images/icons/LaboratoriesLocationOnMap.svg";
import pharmaLocationOnMap from "../../../assets/images/icons/pharmaLocationOnMap.svg";
import RadiologyLocationOnMap from "../../../assets/images/icons/RadiologyLocationOnMap.svg";
import close from "../../../assets/images/icons/x-black.svg";
import { useTranslation } from "react-i18next";
import { IServiceProvider } from "../../../interface/ProfileInterfaces";
import { GeneralButton, GeneralImage, Loader } from "../../../components";
import { useFilter } from "../../../hooks";

interface ServiceProvidersMapProps {
  items: IServiceProvider[];
  readOnly?: boolean;
}

const parseCoordinate = (value: number | string | undefined): number | null => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const ServiceProvidersMap: React.FC<ServiceProvidersMapProps> = ({
  items,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { searchParams } = useFilter();

  const facilityParam = searchParams.get("facility");
  const providerParam = searchParams.get("provider");
  const hasProvider = providerParam;
  const facilityValue = facilityParam || String(items?.[0]?.FacilityTypeId);

  let iconSource: string;
  let size: number;
  let anchor: number;

  switch (facilityValue) {
    case "2":
    case "3":
      iconSource = cursor;
      size = hasProvider ? 200 : 100;
      anchor = hasProvider ? 100 : 50;
      break;
    case "4":
      iconSource = LaboratoriesLocationOnMap;
      size = hasProvider ? 70 : 40;
      anchor = hasProvider ? 50 : 40;
      break;
    case "5":
      iconSource = RadiologyLocationOnMap;
      size = hasProvider ? 70 : 40;
      anchor = hasProvider ? 50 : 40;
      break;
    case "7":
      iconSource = pharmaLocationOnMap;
      size = hasProvider ? 70 : 40;
      anchor = hasProvider ? 50 : 40;
      break;
    default:
      iconSource = cursor;
      size = hasProvider ? 200 : 100;
      anchor = hasProvider ? 100 : 50;
  }

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyB3vI4IQqzxRU1449lbIHThUb2gCeDW47w",
  });

  const validItems = items.filter(
    (item) =>
      parseCoordinate(item.Latitude) !== null &&
      parseCoordinate(item.Longitude) !== null,
  );

  const center = { lat: 33.5138, lng: 36.2765 };
  const [selectedItem, setSelectedItem] = useState<IServiceProvider | null>(
    null,
  );

  if (!isLoaded) return <Loader />;
  return (
    <div className="bg-orange-100 h-[63dvh] w-full overflow-hidden rounded-lg shadow-md">
      <LoadScriptNext googleMapsApiKey="AIzaSyBJoyRoY489sYJoEYbfbyJysIdAJQQLVNw">
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%" }}
          zoom={10}
          center={center}
        >
          {validItems.map((item, index) => {
            const lat = parseCoordinate(item.Latitude)!;
            const lng = parseCoordinate(item.Longitude)!;
            return (
              <MarkerF
                key={index}
                position={{ lat, lng }}
                onClick={() => {
                  if (!readOnly) {
                    setSelectedItem(item);
                  }
                }}
                icon={{
                  url: iconSource,
                  scaledSize: new window.google.maps.Size(size, size),
                  anchor: new window.google.maps.Point(anchor, anchor),
                }}
                title={item.Name}
              />
            );
          })}
          {selectedItem && (
            <InfoWindowF
              position={{
                lat: parseCoordinate(selectedItem.Latitude)!,
                lng: parseCoordinate(selectedItem.Longitude)!,
              }}
              onCloseClick={() => setSelectedItem(null)}
            >
              <div className="rounded-md bg-white p-4 shadow-sm md:min-w-96">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <GeneralImage
                      width="3rem"
                      imageToLoad={doctor}
                      alt={selectedItem.Name}
                      src={selectedItem.Image}
                      className="h-12 w-12 rounded-full shadow-md"
                    />
                    <div className="text-xs">
                      <h3 className="text-sm font-bold">{selectedItem.Name}</h3>
                      <p className="text-body-text">
                        {selectedItem.FacilityType?.Name || ""}
                      </p>
                      <p className="text-body-text">{selectedItem.Address}</p>
                    </div>
                  </div>
                  <img
                    src={close}
                    alt="close"
                    className="cursor-pointer"
                    onClick={() => setSelectedItem(null)}
                  />
                </div>

                <div className="mt-4 flex w-full gap-2 max-sm:flex-wrap">
                  <GeneralButton
                    onClick={() =>
                      navigate(
                        `/search/${selectedItem?.Name?.replaceAll(" ", "-")}/${selectedItem?.Id}/${selectedItem?.ProviderId == "0" ? "location" : "certificates"}?provider=${selectedItem?.ProviderId}`,
                      )
                    }
                    color="lite"
                    className="!w-2/3 !text-base"
                    title={t("show_profile")}
                  />
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${parseCoordinate(
                      selectedItem.Latitude,
                    )},${parseCoordinate(selectedItem.Longitude)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-1 items-center justify-center rounded-lg bg-primary px-5 py-1 text-center text-base font-semibold text-white shadow"
                  >
                    <img className="pe-2" src={markerIcon} alt="marker" />
                    {t("direction")}
                  </a>
                </div>
              </div>
            </InfoWindowF>
          )}
        </GoogleMap>
      </LoadScriptNext>
    </div>
  );
};

export default ServiceProvidersMap;
